import { ClipboardDocumentListIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Chip,
    Column,
    ConfirmDialog,
    DataTable,
    FilterChip,
    Input,
    LinkButton,
    Money,
    PageHeader,
    Segmented,
    Switch,
    notify,
} from '../../components/ui';
import { PREVIEW_LANGUAGES } from '../../constants/languages';
import { OWNER_ABOVE } from '../../constants/roles';
import { useApiError } from '../../hooks/useApiError';
import useAuth from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { AppDispatch, RootState } from '../../store';
import { setCurrentLanguage } from '../../store/slices/languageSlice';
import { Thumb } from './components/Thumb';
import { useAvailability } from './hooks/useAvailability';
import { useRestaurantId, useRestaurantInfo } from './hooks/useRestaurant';
import { useResource } from './hooks/useResource';
import type { ItemRecord } from './types';
import { pickName } from './utils';

type Filter = 'all' | 'available' | 'soldOut';

const MenuItemList = () => {
    const { t } = useTranslation('menu');
    const apiError = useApiError();
    const dispatch = useDispatch<AppDispatch>();
    const { hasRole } = useAuth();
    const canEdit = hasRole(OWNER_ABOVE);
    const restaurantId = useRestaurantId();
    usePageTitle(t('items.title'));

    const items = useResource<ItemRecord[]>(restaurantId ? `/restaurants/${restaurantId}/menu-items` : null);
    const restaurant = useRestaurantInfo(restaurantId);
    const current = useSelector((s: RootState) => s.language.current);
    const supported = restaurant.data?.supportedLanguages ?? [];
    const lang = supported.length === 0 || supported.includes(current) ? current : supported[0];

    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState<Filter>('all');
    const [toDelete, setToDelete] = useState<ItemRecord | null>(null);
    const [deleting, setDeleting] = useState(false);

    const setAvailable = useAvailability((id, isAvailable) =>
        items.setData((list) => list?.map((i) => (i.id === id ? { ...i, isAvailable } : i)) ?? list)
    );

    const nameOf = (i: ItemRecord) => pickName(i.translations, lang) || t('items.untitled');
    const all = useMemo(() => (items.data ?? []).filter((i) => !i.isArchived), [items.data]);
    const counts = useMemo(
        () => ({ all: all.length, available: all.filter((i) => i.isAvailable).length, soldOut: all.filter((i) => !i.isAvailable).length }),
        [all]
    );
    const rows = useMemo(() => {
        const q = query.trim().toLocaleLowerCase();
        return all.filter(
            (i) =>
                (filter === 'all' || (filter === 'available') === i.isAvailable) &&
                (!q || i.translations.some((tr) => tr.name.toLocaleLowerCase().includes(q)))
        );
    }, [all, query, filter]);

    const categoryNames = (i: ItemRecord) =>
        (i.categories ?? [])
            .map((c) => pickName(c.translations, lang))
            .filter(Boolean)
            .join(', ');

    const columns: Column<ItemRecord>[] = [
        {
            id: 'item',
            header: t('items.columns.item'),
            priority: 'primary',
            sortValue: nameOf,
            cell: (i) => (
                <span className="flex items-center gap-3">
                    <Thumb url={i.imageUrl} />
                    <span className="flex min-w-0 flex-col items-start gap-1">
                        <span className="max-w-full truncate">{nameOf(i)}</span>
                        {i.allergens == null && (
                            <Chip variant="action" icon={ExclamationTriangleIcon}>
                                {t('items.noAllergenDeclaration')}
                            </Chip>
                        )}
                    </span>
                </span>
            ),
        },
        {
            id: 'categories',
            header: t('items.columns.categories'),
            priority: 'secondary',
            cell: (i) => categoryNames(i) || <span className="text-muted">—</span>,
        },
        {
            id: 'price',
            header: t('items.columns.price'),
            priority: 'meta',
            align: 'end',
            width: '8rem',
            sortValue: (i) => Number(i.price),
            cell: (i) =>
                restaurant.data ? (
                    <Money amount={i.price} symbol={restaurant.data.currencySymbol} position={restaurant.data.currencySymbolPosition} />
                ) : (
                    i.price
                ),
        },
        {
            id: 'available',
            header: t('items.columns.available'),
            priority: 'secondary',
            width: '11rem',
            cell: (i) => (
                <Switch
                    checked={i.isAvailable}
                    onChange={(next) => void setAvailable(i.id, nameOf(i), next)}
                    label={
                        <>
                            <span aria-hidden="true">{t('items.columns.available')}</span>
                            <span className="sr-only">{t('items.availableFor', { name: nameOf(i) })}</span>
                        </>
                    }
                />
            ),
        },
    ];

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/menu-items/${toDelete.id}`);
            items.setData((list) => list?.filter((i) => i.id !== toDelete.id) ?? list);
            notify.success(t('items.deleted', { name: nameOf(toDelete) }));
            setToDelete(null);
        } catch (e) {
            notify.error(apiError(e));
        } finally {
            setDeleting(false);
        }
    };

    const createButton = canEdit && (
        <LinkButton to="/menu-items/create" variant="strong">
            {t('items.add')}
        </LinkButton>
    );

    return (
        <>
            <PageHeader title={t('items.title')} actions={createButton}>
                <div className="flex flex-col gap-3">
                    {supported.length > 1 && (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-small text-muted">{t('menus.preview')}</span>
                            <Segmented
                                aria-label={t('menus.preview')}
                                value={lang}
                                onChange={(code) => dispatch(setCurrentLanguage(code))}
                                options={supported.map((code) => ({
                                    value: code,
                                    label: PREVIEW_LANGUAGES.find((l) => l.code === code)?.originalLabel ?? code,
                                    lang: code,
                                }))}
                            />
                        </div>
                    )}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="w-full sm:max-w-80">
                            <Input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                aria-label={t('items.search')}
                                placeholder={t('items.search')}
                                leading={<MagnifyingGlassIcon className="size-5" aria-hidden="true" />}
                            />
                        </div>
                        <div className="flex flex-wrap gap-2" role="group" aria-label={t('items.filter')}>
                            {(['all', 'available', 'soldOut'] as const).map((f) => (
                                <FilterChip key={f} pressed={filter === f} onToggle={() => setFilter(f)} count={counts[f]}>
                                    {t(`items.filters.${f}`)}
                                </FilterChip>
                            ))}
                        </div>
                    </div>
                </div>
            </PageHeader>

            <DataTable
                rows={rows}
                columns={columns}
                getRowId={(i) => i.id}
                rowHref={(i) => (canEdit ? `/menu-items/edit/${i.id}` : `/menu-items/${i.id}`)}
                rowLabel={nameOf}
                rowActions={(i) =>
                    canEdit
                        ? [
                              { id: 'edit', label: t('items.edit'), icon: PencilSquareIcon, to: `/menu-items/edit/${i.id}` },
                              { id: 'delete', label: t('items.delete'), icon: TrashIcon, tone: 'danger', separated: true, onSelect: () => setToDelete(i) },
                          ]
                        : []
                }
                state={items.status === 'error' ? 'error' : items.status === 'ready' ? 'ready' : 'loading'}
                onRetry={items.reload}
                errorTitle={t('items.loadFailed')}
                caption={t('items.title')}
                empty={
                    all.length === 0
                        ? { icon: ClipboardDocumentListIcon, title: t('items.emptyTitle'), body: t('items.emptyBody'), action: createButton || undefined }
                        : { icon: MagnifyingGlassIcon, title: t('items.noMatches') }
                }
            />

            <ConfirmDialog
                open={toDelete !== null}
                onClose={() => setToDelete(null)}
                onConfirm={() => void confirmDelete()}
                busy={deleting}
                title={t('items.deleteTitle', { name: toDelete ? nameOf(toDelete) : '' })}
            >
                {t('items.deleteBody')}
            </ConfirmDialog>
        </>
    );
};

export default MenuItemList;
